/**
 * SSE client for canonical Written Chat streaming (R1B.5).
 *
 * ── WHY fetch AND NOT EventSource ──────────────────────────────────────────────
 *
 * The stream is authenticated with a Central bearer token and `EventSource`
 * cannot set headers. Putting the token in a query string would place a
 * credential in proxy logs and browser history, so the stream is read from a
 * `fetch` body instead.
 *
 * ── WHAT THE CALLER MUST NOT DO WITH DELTAS ────────────────────────────────────
 *
 * Accumulated delta text is for DISPLAY ONLY. It is not the conversation. The
 * server sends the persisted turn on `response.completed`, and the caller must
 * reconcile against that — a dropped or duplicated delta would otherwise become
 * a permanent difference between what the user saw and what was recorded.
 *
 * A stream that ends in `failed` or `cancelled` persisted NOTHING, however much
 * text already arrived.
 */

export interface StreamedMessage {
  id: string;
  sessionId: string;
  role: 'user' | 'coach' | 'system';
  content: string;
  turnNumber: number;
  createdAt?: string;
  timestamp?: string;
}

export interface StreamCompleted {
  userMessage: StreamedMessage;
  coachMessage: StreamedMessage;
  turnNumber: number;
  durationMs: number;
}

export interface StreamFailure {
  message: string;
  retryable: boolean;
  preservedUserMessage?: string;
  /**
   * Machine-readable reason, when the server gave one. Only
   * `conversation_engine_disabled` is acted on: it means this deployment has no
   * streaming runtime, so the turn never reached a provider and the caller may
   * safely send it once over the non-streaming endpoint instead. Every other
   * failure — including a provider error mid-stream — must NOT be retried on
   * another transport.
   */
  code?: string;
}

/** The server states this deployment has no canonical streaming runtime. */
export const ENGINE_DISABLED = 'conversation_engine_disabled';

export interface ConversationStreamHandlers {
  onStarted?: (at: number) => void;
  onDelta?: (text: string) => void;
  onCompleted?: (data: StreamCompleted) => void;
  onFailed?: (data: StreamFailure) => void;
  onCancelled?: (data: StreamFailure) => void;
}

export type StreamOutcome = 'completed' | 'failed' | 'cancelled';

const API_URL = import.meta.env.VITE_API_URL || '/api/v1';

/**
 * Minimal SSE frame parser.
 *
 * Frames are separated by a blank line and may be split across network chunks at
 * any byte, so a partial frame is held until its terminator arrives rather than
 * being parsed optimistically.
 */
function parseFrames(buffer: string): { frames: string[]; rest: string } {
  const normalised = buffer.replace(/\r\n/g, '\n');
  const parts = normalised.split('\n\n');
  const rest = parts.pop() ?? '';
  return { frames: parts.filter((p) => p.trim().length > 0), rest };
}

function readFrame(frame: string): { event: string; data: string } {
  let event = 'message';
  const dataLines: string[] = [];
  for (const line of frame.split('\n')) {
    if (line.startsWith(':')) continue; // comment / keepalive
    if (line.startsWith('event:')) event = line.slice(6).trim();
    else if (line.startsWith('data:')) dataLines.push(line.slice(5).replace(/^ /, ''));
  }
  return { event, data: dataLines.join('\n') };
}

/**
 * Send a message and stream the reply.
 *
 * Resolves with how the turn ENDED. It never throws for an ordinary provider
 * failure — that is a `failed` outcome the UI must render, not an exception.
 */
export async function streamCoachingMessage(
  sessionId: string,
  content: string,
  token: string | null | undefined,
  handlers: ConversationStreamHandlers,
  signal?: AbortSignal
): Promise<StreamOutcome> {
  const res = await fetch(`${API_URL}/coaching/sessions/${sessionId}/messages/stream`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'text/event-stream',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: JSON.stringify({ content }),
    signal,
  });

  if (!res.ok || !res.body) {
    let message = 'The conversation model could not be reached. Please try again.';
    let code: string | undefined;
    try {
      const j = await res.json();
      if (j?.message) message = j.message;
      if (typeof j?.code === 'string') code = j.code;
    } catch {
      /* non-JSON error body; keep the default */
    }
    handlers.onFailed?.({
      message,
      retryable: res.status >= 500 || res.status === 429,
      code,
    });
    return 'failed';
  }

  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';
  let outcome: StreamOutcome | null = null;

  try {
    for (;;) {
      const { done, value } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });

      const { frames, rest } = parseFrames(buffer);
      buffer = rest;

      for (const raw of frames) {
        const { event, data } = readFrame(raw);
        let payload: unknown = null;
        try {
          payload = data ? JSON.parse(data) : null;
        } catch {
          continue; // never guess at a malformed frame
        }

        if (event === 'response.started') {
          handlers.onStarted?.((payload as { at: number })?.at ?? Date.now());
        } else if (event === 'response.delta') {
          handlers.onDelta?.((payload as { text: string }).text);
        } else if (event === 'response.completed') {
          outcome = 'completed';
          handlers.onCompleted?.(payload as StreamCompleted);
        } else if (event === 'response.failed') {
          outcome = 'failed';
          handlers.onFailed?.(payload as StreamFailure);
        } else if (event === 'response.cancelled') {
          outcome = 'cancelled';
          handlers.onCancelled?.(payload as StreamFailure);
        }
      }
      if (outcome) break;
    }
  } catch (err) {
    // An aborted read is a cancellation, not a failure.
    if ((err as Error)?.name === 'AbortError') {
      handlers.onCancelled?.({ message: 'Cancelled.', retryable: true });
      return 'cancelled';
    }
    handlers.onFailed?.({
      message: 'The connection was interrupted before the reply finished.',
      retryable: true,
    });
    return 'failed';
  } finally {
    try { await reader.cancel(); } catch { /* already closed */ }
  }

  if (!outcome) {
    // The stream ended without a terminal event. Partial text is NOT a reply.
    handlers.onFailed?.({
      message: 'The reply ended unexpectedly. Please try again.',
      retryable: true,
    });
    return 'failed';
  }
  return outcome;
}
